import { useIsAdvancedViewMode } from "@/v2-backoffice-client/settings/settings.config"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import { useUserViewQuery } from "@/v2-backoffice-client/user/view/user.view.query"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import Tabs from "@/v2-backoffice-client/components/tabs"
import UserTenants from "@/v2-backoffice-client/user/tenant/UserTenants"
import UserSignIn from "@/v2-backoffice-client/user/signIn/UserSignIn"
import UserLoginPanel from "@/v2-backoffice-client/userLogin/list/UserLoginPanel"
import UserFeatureAccess from "@/v2-backoffice-client/user/featureAccess/UserFeatureAccess"
import UserMfa from "@/v2-backoffice-client/user/mfa/UserMfa"
import UserUsage from "@/v2-backoffice-client/user/usage/UserUsage"
import UserViewInfo from "@/v2-backoffice-client/user/view/UserViewInfo"
import UserUpdateConfiguration from "@/v2-backoffice-client/user/update/UserUpdateConfiguration"
import UserViewToolbar from "@/v2-backoffice-client/user/view/UserViewToolbar"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

function UserViewSheet() {
  const isAdvanced = useIsAdvancedViewMode()
  const { id } = useAppViewSheetOpener()
  const userId = Number(id)

  const { data, loading, error } = useUserViewQuery({
    skip: !userId,
    variables: {
      id: userId!,
    },
  })

  const user = data?.getUserById

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading}>
          {formatUserName(user?.FirstName, user?.LastName)}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <UserViewInfo user={user} isLoading={loading} />
        <UserViewToolbar user={user} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="user-view-tabs"
        defaultValue="teams"
        emptyLabel="User not found."
        isLoading={loading}
        error={error}
        isEmpty={!user}
      >
        <Tabs.List>
          <Tabs.Trigger
            value="teams"
            isLoading={loading}
            count={(user?.TenantCount ?? 0) + (user?.RemovedTenantCount ?? 0)}
          >
            Teams
          </Tabs.Trigger>
          <Tabs.Trigger value="feature-access">What they get</Tabs.Trigger>
          <Tabs.Trigger value="sign-ins">Activity</Tabs.Trigger>
          <Tabs.Trigger value="usage">Usage</Tabs.Trigger>
          <Tabs.Trigger value="mfa">MFA</Tabs.Trigger>
          {isAdvanced && (
            <Tabs.Trigger value="configuration">Configuration</Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value="teams">
          {user && (
            <UserTenants
              userId={user.Id}
              userName={formatUserName(user.FirstName, user.LastName)}
              activeCount={user.TenantCount}
              removedCount={user.RemovedTenantCount}
            />
          )}
        </Tabs.Content>

        {/* The customer tab's answer, read for this one person: what reaches
            them and why, row by row, nothing editable here. */}
        <Tabs.Content value="feature-access" className="min-h-0 overflow-auto">
          {user && <UserFeatureAccess user={user} />}
        </Tabs.Content>

        <Tabs.Content value="sign-ins" className="min-h-0 overflow-auto">
          {user && (
            <div className="flex flex-col gap-6">
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Active logins</h3>
                <UserLoginPanel
                  userId={user.Id}
                  tableId="user-view-sessions-table"
                />
              </section>
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Sign-in history</h3>
                <UserSignIn userId={user.Id} />
              </section>
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="usage" className="min-h-0 overflow-auto">
          {user && (
            <UserUsage
              userId={user.Id}
              subject={formatUserName(user.FirstName, user.LastName)}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="mfa" className="min-h-0">
          {user && <UserMfa userId={user.Id} />}
        </Tabs.Content>

        {isAdvanced && (
          <Tabs.Content value="configuration" className="flex-1 p-3">
            {user && (
              <UserUpdateConfiguration
                userId={user.Id}
                configuration={user.Configuration}
              />
            )}
          </Tabs.Content>
        )}
      </Tabs>
    </>
  )
}

export default UserViewSheet
